import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { moviesApi, reviewsApi, languageToTMDB } from '../services/api';
import { ArrowLeft, Clock, Calendar, User, Star } from 'lucide-react';

const MovieDetailPage = () => {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const [movie, setMovie] = useState(null);
  const [review, setReview] = useState(null);
  const [similarMovies, setSimilarMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const tmdbLang = languageToTMDB[language] || 'en-US';

        // Fetch movie details
        const movieData = await moviesApi.getDetail(id, tmdbLang);
        setMovie(movieData);

        // Check if we have a review for this movie
        try {
          const reviewData = await reviewsApi.getByMovie(id);
          setReview(reviewData);
        } catch {
          // No review exists
          setReview(null);
        }

        // Fetch similar movies
        const similarData = await moviesApi.getSimilar(id, 1, tmdbLang);
        setSimilarMovies(similarData.results.slice(0, 6));
      } catch (err) {
        console.error('Error fetching movie:', err);
        setError('Movie not found');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, language]);

  if (loading) {
    return (
      <div className="bg-[#0B0F0E] min-h-screen">
        <Header />
        <main className="pt-32 pb-20 flex items-center justify-center">
          <div className="w-12 h-12 border-2 border-[#52ea88] border-t-transparent animate-spin"></div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !movie) {
    return (
      <div className="bg-[#0B0F0E] min-h-screen">
        <Header />
        <main className="pt-32 pb-20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%] text-center">
            <h1 className="text-4xl font-semibold text-[#E6FFF6] mb-4">Movie Not Found</h1>
            <Link to="/" className="text-[#52ea88] hover:underline">
              Return Home
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // If we have a review, redirect to review page
  if (review) {
    return (
      <div className="bg-[#0B0F0E] min-h-screen">
        <Header />
        <main className="pt-32 pb-20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%] text-center">
            <h1 className="text-4xl font-semibold text-[#E6FFF6] mb-4">Review Available</h1>
            <Link to={`/review/${review.id}`} className="text-[#52ea88] hover:underline">
              Read our full review of {movie.title}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const genres = movie.genres || [];

  return (
    <div className="bg-[#0B0F0E] min-h-screen">
      <Header />

      {/* Hero Banner */}
      <section className="relative h-[60vh] min-h-[500px] overflow-hidden">
        <div className="absolute inset-0">
          {movie.backdrop ? (
            <img
              src={movie.backdrop}
              alt={movie.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-[#121917]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F0E] via-[#0B0F0E]/70 to-[#0B0F0E]/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F0E]/80 to-transparent" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-[7.6923%] h-full flex items-end pb-12">
          <div className="grid lg:grid-cols-3 gap-8 w-full items-end">
            {/* Poster */}
            <div className="hidden lg:block">
              {movie.poster && (
                <img
                  src={movie.poster}
                  alt={movie.title}
                  className="w-full max-w-[280px] border border-[#52ea88]/20 shadow-2xl"
                />
              )}
            </div>

            {/* Info */}
            <div className="lg:col-span-2 space-y-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-[#52ea88] hover:text-[#E6FFF6] transition-colors text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#E6FFF6] leading-tight">
                {movie.title}
              </h1>

              {movie.tagline && (
                <p className="text-xl text-[#52ea88] italic">"{movie.tagline}"</p>
              )}

              <div className="flex flex-wrap items-center gap-4 text-[#9FBFB4]">
                {movie.year && (
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#52ea88]" />
                    {movie.year}
                  </span>
                )}
                {movie.runtime && (
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#52ea88]" />
                    {movie.runtime} min
                  </span>
                )}
                {movie.director && (
                  <span className="flex items-center gap-2">
                    <User className="w-4 h-4 text-[#52ea88]" />
                    {movie.director}
                  </span>
                )}
                {movie.vote_average > 0 && (
                  <span className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#52ea88]" />
                    {movie.vote_average.toFixed(1)}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {genres.map((g) => (
                  <span
                    key={g.id}
                    className="px-3 py-1 border border-[#52ea88]/30 text-[#52ea88] text-sm"
                  >
                    {g.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <main className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Overview */}
              <section className="space-y-4">
                <h2 className="text-2xl font-semibold text-[#E6FFF6] flex items-center gap-3">
                  <span className="w-8 h-0.5 bg-[#52ea88]"></span>
                  Overview
                </h2>
                <p className="text-[#9FBFB4] text-lg leading-relaxed">
                  {movie.overview || 'No overview available.'}
                </p>
              </section>

              {/* No Review Notice */}
              <div className="p-6 bg-[#52ea88]/5 border border-[#52ea88]/20">
                <h3 className="text-lg font-semibold text-[#52ea88] mb-2">
                  Review Coming Soon
                </h3>
                <p className="text-[#9FBFB4]">
                  We haven't published our Matrix Analysis for this film yet.
                  Check back soon for our in-depth review with Matrix Signals evaluation.
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Movie Info Card */}
              <div className="p-6 bg-[#121917] border border-[#52ea88]/10 space-y-4">
                <h3 className="text-lg font-semibold text-[#E6FFF6]">Film Details</h3>
                <div className="space-y-3 text-sm">
                  {movie.director && (
                    <div className="flex justify-between">
                      <span className="text-[#9FBFB4]">Director</span>
                      <span className="text-[#E6FFF6]">{movie.director}</span>
                    </div>
                  )}
                  {movie.year && (
                    <div className="flex justify-between">
                      <span className="text-[#9FBFB4]">Year</span>
                      <span className="text-[#E6FFF6]">{movie.year}</span>
                    </div>
                  )}
                  {movie.runtime && (
                    <div className="flex justify-between">
                      <span className="text-[#9FBFB4]">Runtime</span>
                      <span className="text-[#E6FFF6]">{movie.runtime} min</span>
                    </div>
                  )}
                  {genres.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-[#9FBFB4]">Genre</span>
                      <span className="text-[#E6FFF6] text-right">
                        {genres.map((g) => g.name).join(', ')}
                      </span>
                    </div>
                  )}
                  {movie.vote_average > 0 && (
                    <div className="flex justify-between">
                      <span className="text-[#9FBFB4]">TMDB Rating</span>
                      <span className="text-[#E6FFF6]">{movie.vote_average.toFixed(1)} / 10</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Similar Films */}
              {similarMovies.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-[#E6FFF6]">Similar Films</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {similarMovies.map((m) => (
                      <Link
                        key={m.id}
                        to={`/movie/${m.id}`}
                        className="group block bg-[#121917] hover:bg-[#52ea88]/5 border border-[#52ea88]/10 hover:border-[#52ea88]/30 transition-all overflow-hidden"
                      >
                        {m.poster ? (
                          <img
                            src={m.poster}
                            alt={m.title}
                            className="w-full aspect-[2/3] object-cover"
                          />
                        ) : (
                          <div className="w-full aspect-[2/3] bg-[#0B0F0E] flex items-center justify-center">
                            <span className="text-[#9FBFB4]/50 text-xs">No Image</span>
                          </div>
                        )}
                        <div className="p-2">
                          <h4 className="text-[#E6FFF6] group-hover:text-[#52ea88] transition-colors text-xs font-medium line-clamp-1">
                            {m.title}
                          </h4>
                          <p className="text-[#9FBFB4] text-xs">{m.year}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MovieDetailPage;
